import { GetOptions } from "@/core";
import { useMemo } from "react";
import { useMonthlyReportsPaginated } from "./repository-hooks/report/use-reports";

/**
 * Custom hook to extract available months for a specific year from monthly reports data
 * Returns months in ascending order (January to December) for better UX
 */
export const useAvailableMonths = (selectedYear?: string) => {
  const options: GetOptions = {
    pagination: { limit: 100 },
    orderBy: { field: "dataAsOf", direction: "desc" },
  };
  const monthlyData = useMonthlyReportsPaginated(options);
  const reports = useMemo(
    () => (monthlyData.data ? monthlyData.data.pages.flat() : []),
    [monthlyData.data],
  );

  const availableMonths = useMemo(() => {
    if (
      !reports ||
      reports.length === 0 ||
      !selectedYear ||
      selectedYear === "all"
    ) {
      return [];
    }

    const yearNumber = parseInt(selectedYear, 10);
    if (isNaN(yearNumber)) {
      return [];
    }

    // Extract unique months for the selected year
    const monthsSet = new Set<number>();
    reports
      .filter((report) => report.year === yearNumber)
      .forEach((report) => {
        monthsSet.add(report.month);
      });

    // Convert to array and sort in ascending order (January to December)
    return Array.from(monthsSet).sort((a, b) => a - b);
  }, [reports, selectedYear]);

  return {
    availableMonths,
    isLoading: monthlyData.isLoading,
    error: monthlyData.error,
    hasMonths: availableMonths.length > 0,
  };
};
